import { AddRouteToRoot, HighlightMatchLetters } from "./search-helpers";
import {
    SearchFilterEventEventData,
} from "./search-events";
import SearchResultModel, { ListingItem, SearchOptions } from "./search-model";

export default class SearchSuggestions {
    //Element

    searchFormContainer: HTMLDivElement;
    suggestionsContainer: HTMLElement;
    suggestionsList: HTMLUListElement;
    suggestionItems: HTMLElement[] = [];
    suggestionItemTemplate: HTMLElement;
    searchForm: HTMLFormElement;
    searchInput: HTMLInputElement;
    submitButton: HTMLElement;

    //Result
    totalNumberOfResults: number;

    //Endpoints
    searchOptions: SearchOptions;
    controllerURL: string;
    searchResultPageURL: string;

    //Results
    itemsToShow = 4;

    //Culture
    culture: string;

    //Filters
    Filters: SearchFilterEventEventData[] = [];
    LastSearch: string;

    constructor(searchFormId: string, searchPageURL?: string, searchOptions?: string, initialItemsToShow?: number) {
        this.searchFormContainer = document.getElementById(searchFormId) as HTMLDivElement;
        if (searchOptions) {
            this.searchOptions = JSON.parse(searchOptions);
            this.controllerURL = AddRouteToRoot(this.searchOptions.controllerURL);
        }
        this.searchResultPageURL = searchPageURL?.replace("~", "") ?? "";
        this.itemsToShow = initialItemsToShow;
        this.SetElements();
        this.UpdateResults = this.UpdateResults.bind(this);
        this.AttachEventListeners();

    }

    SetElements() {
        //Suggestions
        this.searchForm = this.searchFormContainer.querySelector("form") as HTMLFormElement;
        this.suggestionsContainer = this.searchFormContainer.querySelector(".search-suggestions__container") as HTMLDivElement;
        this.suggestionsList = this.searchFormContainer.querySelector(".search-suggestions__container ul") as HTMLUListElement;
        this.searchInput = this.searchForm.querySelector("input") as HTMLInputElement;
        this.suggestionItemTemplate = this.suggestionsContainer.querySelector('.search-suggestions__template');
    }
    AttachEventListeners() {
        const searchButton: HTMLButtonElement = document.querySelector('#mobile-search-btn');
        const mobileSearchContainer: HTMLDivElement = document.querySelector('#mobile-search');
        let timeout: NodeJS.Timeout | null = null;
        this.searchInput.addEventListener("input", () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                this.Search(this.searchInput.value);
            }, 500);
        });
        this.searchInput.addEventListener("focusin", () => {
            if (this.suggestionItems.length > 0)
                this.ShowSuggestions();
        });
        this.searchInput.addEventListener("blur", () => {
            setTimeout(() => {
                const activeElement = document.activeElement;
                if (!this.searchFormContainer.contains(activeElement)) {
                    this.HideSuggestions();
                }
            }, 300);
        });
        this.searchInput.addEventListener('keydown', (e) => {
            if (this.searchInput.value == "")
                return;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                this.suggestionItems[0].querySelector('a').focus();
            }
            else if (e.key === "Tab") {
                e.preventDefault();
                if (!e.shiftKey) {
                    this.suggestionItems[0].querySelector('a').focus();
                }
            }
        })
        this.searchFormContainer.addEventListener('focusout', (e: FocusEvent) => {
            const target = e.relatedTarget as HTMLElement;

            if (target && !this.suggestionsContainer.contains(target)) {
                this.HideSuggestions();
            }
        });
        this.suggestionsList.addEventListener('keydown', (e) => {
            const target = e.target as HTMLAnchorElement;
            const firstListElement = this.suggestionItems[0].querySelector('a') as HTMLAnchorElement;
            const lastListElement = this.suggestionItems[this.suggestionItems.length - 1].querySelector('a') as HTMLAnchorElement;
            const viewAllBtn = this.suggestionsContainer.querySelector('.view-all a') as HTMLAnchorElement;
            const submitBtn = this.searchFormContainer.querySelector('.search-input__button') as HTMLButtonElement;
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    const nextSibling = target.closest('li')?.nextElementSibling;
                    if (nextSibling && nextSibling instanceof HTMLLIElement) {
                        const nextAnchor = nextSibling.querySelector('a') as HTMLAnchorElement;
                        if (nextAnchor) nextAnchor.focus();
                    }
                    break;

                case "ArrowUp":
                    e.preventDefault();
                    const prevSibling = target.closest('li')?.previousElementSibling;
                    if (prevSibling) {
                        const prevAnchor = prevSibling.querySelector('a') as HTMLAnchorElement;
                        if (prevAnchor) prevAnchor.focus();
                    }
                    break;

                case "Tab":
                    if (e.shiftKey) { // Shift + Tab
                        if (document.activeElement === firstListElement) {
                            e.preventDefault();
                            this.searchInput.focus();
                        } else if (document.activeElement === viewAllBtn) {
                            e.preventDefault();
                            lastListElement.focus();
                        } else {
                            e.preventDefault();
                            const prevSibling = target.closest('li')?.previousElementSibling;
                            if (prevSibling) {
                                const prevAnchor = prevSibling.querySelector('a') as HTMLAnchorElement;
                                if (prevAnchor) prevAnchor.focus();
                            }
                        }
                    } else {
                        if (document.activeElement === viewAllBtn) {
                            e.preventDefault();
                            submitBtn.focus();
                        }
                    }
                    break;

                case "Escape":
                    this.suggestionsContainer.classList.add('hide');
                    break;
            }
        })
        this.searchForm.addEventListener("submit", (e) => {
            e.preventDefault()
            const query = this.searchInput.value.trim();
            if (query == "") {
                this.ShowErrorMessage();
            }
            else {
                window.location.href = `${this.searchResultPageURL}?keyword=${encodeURIComponent(query)}`;
            }
            this.searchInput.value = "";
        })
        searchButton.addEventListener('click', () => {
            if (mobileSearchContainer.classList.contains('show')) {
                searchButton.setAttribute('aria-expanded', 'false')
            } else {
                searchButton.setAttribute('aria-expanded', 'true')
            }
        })
    }

    async Search(query: string) {
        if (!query || query.length < 1) {
            this.ResetSuggestionsList();
            return;
        }

        this.LastSearch = query;
        const url = `${this.controllerURL}?query=${encodeURIComponent(query)}&page=1&itemsPerPage=${this.itemsToShow}&searchIndexes=${this.searchOptions.searchIndexes}&isSuggestionMode=True`;

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Search API error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            this.UpdateResults(data);
        } catch (error) {
            console.error("Search request failed:", error);
        }
    }

    UpdateResults(jsonObj: SearchResultModel<ListingItem>) {
        if (!this.suggestionsContainer) return;

        this.suggestionsList.innerHTML = '';
        this.suggestionItems = [];
        this.totalNumberOfResults = jsonObj.TotalNumberOfResults;

        for (let i: number = 0; i < jsonObj.Items?.length; i++) {
            const suggestionItemClone: HTMLDivElement = this.suggestionItemTemplate.cloneNode(true) as HTMLDivElement;
            const suggestionItemTitle: HTMLDivElement = suggestionItemClone.querySelector(".result-item__title") as HTMLHeadingElement;
            const suggestionItemLink: HTMLAnchorElement = suggestionItemClone.querySelector("a");
            if (suggestionItemClone != null &&
                suggestionItemTitle != null) {
                //Assign JSON values to template
                suggestionItemClone.classList.remove('d-none');
                suggestionItemClone.classList.remove('search-suggestions__template');
                suggestionItemClone.setAttribute('title', jsonObj.Items[i].Title);
                suggestionItemLink.classList.add('search-suggestions__item');
                const itemTitle = jsonObj.Items[i].Title.length > 55 ? jsonObj.Items[i].Title.substring(0, 54) + "..." : jsonObj.Items[i].Title;
                suggestionItemTitle.innerHTML = HighlightMatchLetters(this.LastSearch, itemTitle);

                if (jsonObj.Items[i].URL != null) {
                    suggestionItemLink.href = jsonObj.Items[i].URL.replace("~/", "");
                    suggestionItemLink.tabIndex = 0;
                }
            }

            this.suggestionItems.push(suggestionItemClone);
        }

        this.GenerateSuggestionsList();

        if (this.suggestionsContainer && this.suggestionItems.length > 0) {
            this.ShowSuggestions();
            this.ShowSuggestionsAvailableMessage();
        }
    }

    // Generate suggstions list
    GenerateSuggestionsList() {
        for (let i = 0; i < this.suggestionItems.length; i++) {
            this.suggestionsList.appendChild(this.suggestionItems[i]);
        }
        if (this.suggestionItems.length < this.totalNumberOfResults) {
            // Create "View all" link after appending the first 5 items
            const viewAllLink = document.createElement('a');
            const viewAllWrapper = document.createElement('div');
            viewAllWrapper.classList.add('view-all');
            viewAllLink.classList.add('h5');
            viewAllLink.innerHTML = `See all ${this.totalNumberOfResults} results<span class="dot-icon dot-stroke-forward-arrow dot-icon-bold"></span>`;
            viewAllLink.href = `/search?keyword=${encodeURIComponent(this.LastSearch)}`;
            viewAllWrapper.appendChild(viewAllLink);
            this.suggestionsList.appendChild(viewAllWrapper);
        }
    }
    ResetSuggestionsList() {
        this.suggestionsList.innerHTML = ''
        this.HideSuggestions();
    }
    ShowSuggestions() {
        this.suggestionsContainer.classList.add('show');
        this.suggestionsContainer.classList.remove('hide');
        this.searchInput.setAttribute('aria-expanded', 'true');
        (this.suggestionsList.querySelectorAll('a, button') as NodeListOf<HTMLElement>).forEach(btn => btn.tabIndex = 0)
    }
    HideSuggestions() {
        this.suggestionsContainer.classList.remove('show');
        this.suggestionsContainer.classList.add('hide');
        this.searchInput.setAttribute('aria-expanded', 'false');
        (this.suggestionsList.querySelectorAll('a, button') as NodeListOf<HTMLElement>).forEach(btn => btn.tabIndex = -1)
    }
    ShowErrorMessage() {
        const liveRegion = this.searchForm.querySelector('.live-region');
        setTimeout(() => {
            liveRegion.textContent = 'Please enter query text to submit the search.';
            this.searchInput.classList.add("error");
            setTimeout(() => {
                liveRegion.textContent = '';
                this.searchInput.classList.remove("error");
            }, 3000);

        }, 200);
    }
    ShowSuggestionsAvailableMessage() {
        const liveRegion = this.searchForm.querySelector('.live-region');
        setTimeout(() => {
            liveRegion.textContent = 'Suggestions available';
            setTimeout(() => {
                liveRegion.textContent = '';
            }, 3000);

        }, 200);
    }
}